import { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { 
  Building2, Plus, Search, Filter, MoreVertical, Eye, Edit, 
  Power, PowerOff, Trash2, Copy, RefreshCw, Users, MapPin, X, Upload, Loader2, Mail, AlertCircle, ExternalLink, User
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import platformApi from '@/lib/platformApi';

const SUBSCRIPTION_TIERS = ['basic', 'professional', 'enterprise'];
const STATUS_OPTIONS = ['active', 'suspended', 'trial', 'expired'];

// Get the base URL for the hospital portal
const getPortalBaseUrl = () => {
  // Use the current origin or fallback to production URL
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return 'https://zestahealthcare.com';
};

export default function HospitalManagement() {
  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTier, setFilterTier] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedHospital, setSelectedHospital] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newHospital, setNewHospital] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    country: 'India',
    license_number: '',
    subscription_tier: 'basic',
    admin_name: '',
    admin_email: '',
    admin_phone: '',
    // Branding
    logo_url: '',
    tagline: '',
    primary_color: '#4A238F',
    secondary_color: '#7C3AED',
    // Sample data option (defaults to false for production)
    seed_sample_data: false
  });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);

  useEffect(() => {
    fetchHospitals();
  }, []);

  const fetchHospitals = async () => {
    try {
      const response = await platformApi.get('/platform/hospitals');
      setHospitals(response.data);
    } catch (error) {
      toast.error('Failed to load hospitals');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await platformApi.post('/uploads/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Construct full URL
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      const logoUrl = `${baseUrl}${response.data.url}`;
      
      setNewHospital(prev => ({ ...prev, logo_url: logoUrl }));
      toast.success('Logo uploaded successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const filteredHospitals = useMemo(() => {
    return hospitals.filter(hospital => {
      const matchesSearch = !searchQuery || 
        hospital.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        hospital.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        hospital.city?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesTier = !filterTier || hospital.subscription_tier === filterTier;
      const matchesStatus = !filterStatus || hospital.subscription_status === filterStatus;
      return matchesSearch && matchesTier && matchesStatus;
    });
  }, [hospitals, searchQuery, filterTier, filterStatus]);

  const handleCreateHospital = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const response = await platformApi.post('/platform/hospitals', newHospital);
      
      toast.success('Hospital created successfully!');
      
      // Show sample data notification if seeded
      if (response.data.sample_data_seeded) {
        toast.info(
          'Sample data (5 patients, 2 visits) has been added for demo purposes.',
          { duration: 5000 }
        );
      }
      
      // Show admin credentials
      if (response.data.admin_temp_password) {
        toast.info(
          `Admin credentials:\nEmail: ${newHospital.admin_email}\nTemp Password: ${response.data.admin_temp_password}`,
          { duration: 10000 }
        );
      }
      
      setShowCreateModal(false);
      setNewHospital({
        name: '', email: '', phone: '', address: '', city: '', state: '',
        country: 'India', license_number: '', subscription_tier: 'basic',
        admin_name: '', admin_email: '', admin_phone: '',
        logo_url: '', tagline: '', primary_color: '#4A238F', secondary_color: '#7C3AED',
        seed_sample_data: false
      });
      fetchHospitals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create hospital');
    } finally {
      setCreating(false);
    }
  };

  const handleStatusChange = async (hospitalId, newStatus) => {
    try {
      await platformApi.put(`/platform/hospitals/${hospitalId}/status?status=${newStatus}`);
      toast.success(`Hospital ${newStatus === 'active' ? 'activated' : 'suspended'}`);
      fetchHospitals();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const copyLicenseKey = (key) => {
    navigator.clipboard.writeText(key);
    toast.success('License key copied to clipboard');
  };

  const handleResendSetupEmail = async (hospitalId) => {
    setResendingEmail(true);
    try {
      const response = await platformApi.post(`/platform/hospitals/${hospitalId}/resend-setup-email`);
      toast.success('Setup email sent successfully!');
      
      // If email sending failed but token was generated, show the link
      if (response.data.setup_link) {
        toast.info(`Setup link: ${response.data.setup_link}`, { duration: 15000 });
      }
      
      // Refresh hospital data
      fetchHospitals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend setup email');
    } finally {
      setResendingEmail(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Hospital Management</h1>
          <p className="text-slate-400">Manage all hospital tenants on the platform</p>
        </div>
        <Button 
          onClick={() => setShowCreateModal(true)}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="create-hospital-btn"
        >
          <Plus className="mr-2 h-4 w-4" />
          Onboard Hospital
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search hospitals..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                data-testid="search-hospitals-input"
              />
            </div>
            <select
              value={filterTier}
              onChange={(e) => setFilterTier(e.target.value)}
              className="px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
              data-testid="filter-tier-select"
            >
              <option value="">All Tiers</option>
              {SUBSCRIPTION_TIERS.map(tier => (
                <option key={tier} value={tier} className="capitalize">{tier}</option>
              ))}
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
              data-testid="filter-status-select"
            >
              <option value="">All Status</option>
              {STATUS_OPTIONS.map(status => (
                <option key={status} value={status} className="capitalize">{status}</option>
              ))}
            </select>
            <Button 
              variant="outline" 
              onClick={fetchHospitals}
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results count */}
      <p className="text-sm text-slate-400">
        Showing {filteredHospitals.length} of {hospitals.length} hospitals
      </p>

      {/* Hospitals Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredHospitals.map((hospital) => (
            <Card key={hospital.id} className="bg-slate-800/50 border-slate-700 hover:border-slate-600 transition-colors">
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg">
                      {hospital.name?.charAt(0) || 'H'}
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">{hospital.name}</h3>
                      <p className="text-xs text-slate-400 flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {hospital.city || 'N/A'}, {hospital.state || 'N/A'}
                      </p>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white hover:bg-slate-700">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-slate-800 border-slate-700">
                      <DropdownMenuItem 
                        className="text-slate-300 hover:bg-slate-700"
                        onClick={() => { setSelectedHospital(hospital); setShowDetailsModal(true); }}
                      >
                        <Eye className="mr-2 h-4 w-4" /> View Details
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        className="text-slate-300 hover:bg-slate-700"
                        onClick={() => copyLicenseKey(hospital.license_key)}
                      >
                        <Copy className="mr-2 h-4 w-4" /> Copy License Key
                      </DropdownMenuItem>
                      {hospital.subscription_status === 'active' ? (
                        <DropdownMenuItem 
                          className="text-red-400 hover:bg-slate-700"
                          onClick={() => handleStatusChange(hospital.id, 'suspended')}
                        >
                          <PowerOff className="mr-2 h-4 w-4" /> Suspend
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem 
                          className="text-emerald-400 hover:bg-slate-700"
                          onClick={() => handleStatusChange(hospital.id, 'active')}
                        >
                          <Power className="mr-2 h-4 w-4" /> Activate
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-400">Subscription</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                      hospital.subscription_tier === 'enterprise' ? 'bg-amber-500/20 text-amber-400' :
                      hospital.subscription_tier === 'professional' ? 'bg-purple-500/20 text-purple-400' :
                      'bg-slate-500/20 text-slate-400'
                    }`}>
                      {hospital.subscription_tier}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-400">Status</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                      hospital.subscription_status === 'active' ? 'bg-emerald-500/20 text-emerald-400' :
                      hospital.subscription_status === 'suspended' ? 'bg-red-500/20 text-red-400' :
                      'bg-amber-500/20 text-amber-400'
                    }`}>
                      {hospital.subscription_status}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-400">Patients</span>
                    <span className="text-white font-medium">{hospital.patient_count || 0}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-400">Users</span>
                    <span className="text-white font-medium">{hospital.user_count || 0}</span>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-slate-700 flex items-center justify-between">
                  <p className="text-xs text-slate-500 truncate flex-1" title={hospital.license_key}>
                    License: {hospital.license_key}
                  </p>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleResendSetupEmail(hospital.id);
                    }}
                    disabled={resendingEmail}
                    className={`ml-2 text-xs ${
                      hospital.admin_setup_pending 
                        ? 'text-amber-400 hover:text-amber-300 hover:bg-amber-500/20' 
                        : 'text-slate-400 hover:text-slate-300 hover:bg-slate-700'
                    }`}
                    title={hospital.admin_setup_pending ? "Admin setup pending - Resend setup email" : "Resend setup email to admin"}
                    data-testid={`resend-email-card-btn-${hospital.id}`}
                  >
                    {resendingEmail ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Mail className="h-3 w-3" />
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
          
          {filteredHospitals.length === 0 && (
            <div className="col-span-full py-12 text-center text-slate-500">
              <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No hospitals found matching your criteria</p>
            </div>
          )}
        </div>
      )}

      {/* Create Hospital Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2 text-slate-900">
              <Building2 className="h-5 w-5 text-emerald-600" />
              Onboard New Hospital
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateHospital} className="flex-1 overflow-y-auto space-y-6 pr-2">
            {/* Hospital Info */}
            <div>
              <h3 className="text-sm font-medium text-slate-700 mb-3">Hospital Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-xs text-slate-600 mb-1">Hospital Name *</label>
                  <input
                    type="text"
                    value={newHospital.name}
                    onChange={(e) => setNewHospital({...newHospital, name: e.target.value})}
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="City General Hospital"
                    required
                    data-testid="hospital-name-input"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1">Email *</label>
                  <input
                    type="email"
                    value={newHospital.email}
                    onChange={(e) => setNewHospital({...newHospital, email: e.target.value})}
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="contact@hospital.com"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1">Phone</label>
                  <input
                    type="text"
                    value={newHospital.phone}
                    onChange={(e) => setNewHospital({...newHospital, phone: e.target.value})}
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="+91-9876543210"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1">City</label>
                  <input
                    type="text"
                    value={newHospital.city}
                    onChange={(e) => setNewHospital({...newHospital, city: e.target.value})}
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Mumbai"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1">State</label>
                  <input
                    type="text"
                    value={newHospital.state}
                    onChange={(e) => setNewHospital({...newHospital, state: e.target.value})}
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Maharashtra"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1">License Number</label>
                  <input
                    type="text"
                    value={newHospital.license_number}
                    onChange={(e) => setNewHospital({...newHospital, license_number: e.target.value})}
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="MH-HOSP-2024-XXX"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1">Subscription Tier *</label>
                  <select
                    value={newHospital.subscription_tier}
                    onChange={(e) => setNewHospital({...newHospital, subscription_tier: e.target.value})}
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    required
                    data-testid="subscription-tier-select"
                  >
                    <option value="basic">Basic</option>
                    <option value="professional">Professional</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Admin Info */}
            <div>
              <h3 className="text-sm font-medium text-slate-700 mb-3">Hospital Admin Account</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-xs text-slate-600 mb-1">Admin Name *</label>
                  <input
                    type="text"
                    value={newHospital.admin_name}
                    onChange={(e) => setNewHospital({...newHospital, admin_name: e.target.value})}
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Dr. John Smith"
                    required
                    data-testid="admin-name-input"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1">Admin Email *</label>
                  <input
                    type="email"
                    value={newHospital.admin_email}
                    onChange={(e) => setNewHospital({...newHospital, admin_email: e.target.value})}
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="admin@hospital.com"
                    required
                    data-testid="admin-email-input"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1">Admin Phone</label>
                  <input
                    type="text"
                    value={newHospital.admin_phone}
                    onChange={(e) => setNewHospital({...newHospital, admin_phone: e.target.value})}
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="+91-9876543210"
                  />
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                An email with setup instructions will be sent to the admin.
              </p>
            </div>

            {/* Sample Data Option */}
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newHospital.seed_sample_data}
                      onChange={(e) => setNewHospital({...newHospital, seed_sample_data: e.target.checked})}
                      className="w-5 h-5 text-amber-600 border-amber-300 rounded focus:ring-amber-500 cursor-pointer"
                      data-testid="seed-sample-data-checkbox"
                    />
                    <div>
                      <span className="text-sm font-medium text-slate-700">Seed Sample Data</span>
                      <span className="ml-2 px-2 py-0.5 text-xs bg-amber-200 text-amber-800 rounded-full">Demo/Testing</span>
                    </div>
                  </label>
                  <p className="text-xs text-slate-500 mt-2 ml-8">
                    Enable this option to pre-populate the hospital with sample patients and visits for demonstration or testing purposes. 
                    <strong className="text-amber-700"> Leave unchecked for production accounts.</strong>
                  </p>
                </div>
              </div>
            </div>

            {/* Branding */}
            <div>
              <h3 className="text-sm font-medium text-slate-700 mb-3">Hospital Branding</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-xs text-slate-600 mb-1">Hospital Logo</label>
                  <div className="flex items-start gap-4">
                    {/* Upload Area */}
                    <div className="flex-1">
                      <label 
                        className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                          uploadingLogo 
                            ? 'border-emerald-500 bg-emerald-50' 
                            : 'border-slate-300 hover:border-emerald-500 hover:bg-slate-50'
                        }`}
                      >
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          {uploadingLogo ? (
                            <>
                              <Loader2 className="w-8 h-8 mb-2 text-emerald-500 animate-spin" />
                              <p className="text-sm text-emerald-600">Uploading...</p>
                            </>
                          ) : (
                            <>
                              <Upload className="w-8 h-8 mb-2 text-slate-400" />
                              <p className="mb-1 text-sm text-slate-500">
                                <span className="font-semibold text-emerald-600">Click to upload</span> or drag and drop
                              </p>
                              <p className="text-xs text-slate-400">PNG, JPG, GIF up to 5MB</p>
                            </>
                          )}
                        </div>
                        <input 
                          type="file" 
                          className="hidden" 
                          accept="image/*"
                          onChange={handleLogoUpload}
                          disabled={uploadingLogo}
                          data-testid="logo-upload-input"
                        />
                      </label>
                    </div>
                    
                    {/* Preview */}
                    {newHospital.logo_url && (
                      <div className="relative">
                        <img 
                          src={newHospital.logo_url} 
                          alt="Logo preview" 
                          className="w-32 h-32 object-contain rounded-lg border border-slate-200 bg-white p-2"
                        />
                        <button
                          type="button"
                          onClick={() => setNewHospital({...newHospital, logo_url: ''})}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 rounded-full text-white hover:bg-red-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-2">Recommended: Square image, 200x200px or larger</p>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs text-slate-600 mb-1">Tagline</label>
                  <input
                    type="text"
                    value={newHospital.tagline}
                    onChange={(e) => setNewHospital({...newHospital, tagline: e.target.value})}
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Caring for your health since 1990"
                    data-testid="tagline-input"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1">Primary Color</label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={newHospital.primary_color}
                      onChange={(e) => setNewHospital({...newHospital, primary_color: e.target.value})}
                      className="w-12 h-10 rounded cursor-pointer border border-slate-300"
                    />
                    <input
                      type="text"
                      value={newHospital.primary_color}
                      onChange={(e) => setNewHospital({...newHospital, primary_color: e.target.value})}
                      className="flex-1 px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      placeholder="#4A238F"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1">Secondary Color</label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={newHospital.secondary_color}
                      onChange={(e) => setNewHospital({...newHospital, secondary_color: e.target.value})}
                      className="w-12 h-10 rounded cursor-pointer border border-slate-300"
                    />
                    <input
                      type="text"
                      value={newHospital.secondary_color}
                      onChange={(e) => setNewHospital({...newHospital, secondary_color: e.target.value})}
                      className="flex-1 px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      placeholder="#7C3AED"
                    />
                  </div>
                </div>
              </div>
              {/* Preview */}
              {(newHospital.logo_url || newHospital.tagline || newHospital.name) && (
                <div className="mt-4 p-4 rounded-lg" style={{ background: `linear-gradient(135deg, ${newHospital.primary_color} 0%, ${newHospital.secondary_color} 100%)` }}>
                  <p className="text-xs text-white/60 mb-2">Preview</p>
                  <div className="flex items-center gap-3">
                    {newHospital.logo_url ? (
                      <img src={newHospital.logo_url} alt="Logo" className="w-12 h-12 rounded-lg bg-white p-1 object-contain" onError={(e) => e.target.style.display='none'} />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-white/20 flex items-center justify-center text-white font-bold text-xl">
                        {newHospital.name?.charAt(0) || 'H'}
                      </div>
                    )}
                    <div>
                      <h4 className="text-white font-semibold">{newHospital.name || 'Hospital Name'}</h4>
                      {newHospital.tagline && <p className="text-white/80 text-xs">{newHospital.tagline}</p>}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 sticky bottom-0 bg-white">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowCreateModal(false)}
                className="border-slate-300 text-slate-700 hover:bg-slate-100"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                disabled={creating}
                data-testid="submit-create-hospital-btn"
              >
                {creating ? 'Creating...' : 'Create Hospital'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Hospital Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Hospital Details</DialogTitle>
          </DialogHeader>
          {selectedHospital && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-2xl">
                  {selectedHospital.name?.charAt(0) || 'H'}
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white">{selectedHospital.name}</h3>
                  <p className="text-slate-400">{selectedHospital.email}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-slate-700/50 rounded-lg">
                  <p className="text-xs text-slate-300 font-medium mb-1">Location</p>
                  <p className="text-white">{selectedHospital.city}, {selectedHospital.state}</p>
                </div>
                <div className="p-3 bg-slate-700/50 rounded-lg">
                  <p className="text-xs text-slate-300 font-medium mb-1">Phone</p>
                  <p className="text-white">{selectedHospital.phone || 'N/A'}</p>
                </div>
                <div className="p-3 bg-slate-700/50 rounded-lg">
                  <p className="text-xs text-slate-300 font-medium mb-1">Tier</p>
                  <p className="text-white capitalize">{selectedHospital.subscription_tier}</p>
                </div>
                <div className="p-3 bg-slate-700/50 rounded-lg">
                  <p className="text-xs text-slate-300 font-medium mb-1">Status</p>
                  <p className="text-white capitalize">{selectedHospital.subscription_status}</p>
                </div>
                <div className="p-3 bg-slate-700/50 rounded-lg">
                  <p className="text-xs text-slate-300 font-medium mb-1">Patients</p>
                  <p className="text-white">{selectedHospital.patient_count || 0}</p>
                </div>
                <div className="p-3 bg-slate-700/50 rounded-lg">
                  <p className="text-xs text-slate-300 font-medium mb-1">Users</p>
                  <p className="text-white">{selectedHospital.user_count || 0}</p>
                </div>
              </div>

              {/* Hospital Portal Login URL */}
              {selectedHospital.login_slug && (
                <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                  <p className="text-xs text-emerald-300 font-medium mb-2">Hospital Portal Login URL</p>
                  <div className="flex items-center gap-2">
                    <code className="text-emerald-400 text-sm flex-1 break-all">
                      {getPortalBaseUrl()}/login/{selectedHospital.login_slug}
                    </code>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => {
                        const url = `${getPortalBaseUrl()}/login/${selectedHospital.login_slug}`;
                        navigator.clipboard.writeText(url);
                        toast.success('Portal URL copied to clipboard');
                      }}
                      className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/20"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => {
                        const url = `${getPortalBaseUrl()}/login/${selectedHospital.login_slug}`;
                        window.open(url, '_blank');
                      }}
                      className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/20"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Admin Account Details */}
              <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <User className="h-4 w-4 text-blue-400" />
                  <p className="text-xs text-blue-300 font-medium">Admin Account</p>
                </div>
                <div className="space-y-2">
                  {(selectedHospital.pending_admin_name || selectedHospital.admin_name) && (
                    <div className="flex items-center justify-between">
                      <span className="text-slate-300 text-sm">Name:</span>
                      <span className="text-white text-sm font-medium">
                        {selectedHospital.pending_admin_name || selectedHospital.admin_name}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-slate-300 text-sm">Email:</span>
                    <span className="text-white text-sm font-medium">
                      {selectedHospital.pending_admin_email || selectedHospital.admin_email || 'N/A'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-300 text-sm">Status:</span>
                    <span className={`text-sm font-medium ${selectedHospital.admin_setup_pending ? 'text-amber-400' : 'text-emerald-400'}`}>
                      {selectedHospital.admin_setup_pending ? 'Setup Pending' : 'Active'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-3 bg-slate-700/50 rounded-lg">
                <p className="text-xs text-slate-300 font-medium mb-1">License Key</p>
                <div className="flex items-center gap-2">
                  <code className="text-emerald-400 text-sm flex-1">{selectedHospital.license_key}</code>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => copyLicenseKey(selectedHospital.license_key)}
                    className="text-slate-400 hover:text-white"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="p-3 bg-slate-700/50 rounded-lg">
                <p className="text-xs text-slate-300 font-medium mb-1">Created</p>
                <p className="text-white">{new Date(selectedHospital.created_at).toLocaleDateString()}</p>
              </div>

              {/* Admin Setup Status */}
              {selectedHospital.admin_setup_pending && (
                <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-amber-400 font-medium text-sm">Admin Setup Pending</p>
                      <p className="text-slate-300 text-xs mt-1">
                        The hospital admin hasn't completed their account setup yet. 
                        The setup link may have expired (24h validity).
                      </p>
                      <Button
                        onClick={() => handleResendSetupEmail(selectedHospital.id)}
                        disabled={resendingEmail}
                        className="mt-3 bg-amber-600 hover:bg-amber-700 text-white text-xs"
                        size="sm"
                        data-testid="resend-setup-email-btn"
                      >
                        {resendingEmail ? (
                          <>
                            <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Mail className="mr-2 h-3 w-3" />
                            Resend Setup Email
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
